/**
 * Whether a failed write may still have gone through (place manager, unit 6b;
 * every portal write since). Anything not known to be a clean refusal counts
 * as lost, so a form never offers its send again after a failure that might
 * have saved (a duplicate costs more than a reopened form). Apart from
 * `writes.ts` because it needs the transport's error classes (and the GraphQL
 * client with them), which the page bodies the proofs render must not load.
 */
import { BackendUnavailableError, isMaskedMessage } from "@/lib/backend-error";
import { GraphQLRequestError, type GraphQLErrorEntry } from "@/lib/graphql";

/** 4xx statuses that can come after the request reached the backend (a
 *  timeout, a rate limit after forwarding, the client gone): not a refusal. */
const MAY_HAVE_RUN_4XX = new Set([408, 429, 499]);

/** Errors `src/lib/graphql.ts` raises before any request goes out. */
const BEFORE_SENDING = ["BUN_PUBLIC_GRAPHQL_ENDPOINT is not configured.", "Must be signed in to call the pop.in GraphQL API."];

/** False only for a known clean refusal, nothing made: the backend's own
 *  GraphQL answer refusing the create at its root (no masked error among
 *  them); an HTTP 4xx but 408, 429 and 499 (refused before running); or an error raised before the
 *  request went out (no endpoint, signed out, the Firebase sign-in token).
 *  True for everything else: unreachable, a 5xx, an answer cut off (any error
 *  reading it), no data, a masked "Unexpected error.", an error below the
 *  mutation's root (the event was saved, a field of it failed), anything
 *  unknown (an error after the answer came back means the event was made). */
export function isLostAnswer(error: unknown): boolean {
  if (error instanceof BackendUnavailableError) {
    const refused = error.status !== undefined && error.status >= 400 && error.status < 500 && !MAY_HAVE_RUN_4XX.has(error.status);
    return !refused;
  }
  if (error instanceof GraphQLRequestError) {
    const entries = error.errors as GraphQLErrorEntry[];
    return entries.length === 0 || entries.some((e) => isMaskedMessage(e.message) || (e.path?.length ?? 0) > 1);
  }
  if (error instanceof Error && (error.name === "FirebaseError" || BEFORE_SENDING.includes(error.message))) return false;
  return true;
}
